import { Controller } from "@hotwired/stimulus";
import "./component.scss";

// Connects to data-controller="stripe-checkout"
export default class extends Controller {

  static values = {
    clientSecret: String,
    publishableKey: String,
    returnUrl: String,
    setupIntent: Boolean,
    recurring: Boolean,
    paymentDescription: String,
    managementUrl: String,
    regularBillingProps: Object,
    trialBillingProps: Object,
    // i18n error messages (passed from Rails via data-values)
    errorBillingRequired: String,
    errorTaxIdRequired: String,
    errorBillingSaveFailed: String,
    errorNetwork: String,
    errorUnexpected: String,
    checkoutRepricingUrl: String,
    repricingSubjectType: String,
    repricingSubjectId: Number,
    errorRepricingFailed: String,
    checkoutEligibilityUrl: String,
    checkoutEligibilityMessages: Object,
    eligibilitySnapshotRefreshUrl: String,
    eligibilitySnapshotSubjectType: String,
    eligibilitySnapshotSubjectId: Number,
    errorEligibilityUnauthorized: String,
    errorEligibilityServiceUnavailable: String,
    errorWithdrawalRequired: String,
  }

  static targets = [
    "expressCheckout",
    "expressDivider",
    "payment",
    "messageContainer",
    "submitButton",
    "buttonText",
    "spinner",
    "mobileCheckoutTermsNote",
    "billingForm",
    "billingBusinessField"
  ]

  connect() {
    this.stripe = Stripe(this.publishableKeyValue);
    this.expressCheckoutCountryCode = null;
    this.lastBillingPricingKey = null;
    this.requestedBillingPricingKey = null;
    this.billingPreviewRequestId = 0;
    this.billingPreviewContext = null;

    // Payment Element with accordion style (no gaps between methods)
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      locale: "de",
      appearance: {
        theme: 'stripe',
        variables: {
          borderRadius: '12px',
        }
      }
    });

    // Express Checkout with rounded buttons
    this.expressElements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      locale: "de",
      appearance: {
        theme: 'stripe',
        variables: {
          borderRadius: '9999px',
        }
      }
    });

    this.#initializeCheckout();
    queueMicrotask(() => {
      this.billingContextChanged();
      this.#syncMobileCheckoutTermsNote();
    });
  }

  disconnect() {
  }

  async #initializeCheckout() {
    // Express checkout (Apple Pay, Google Pay, PayPal)
    if (this.hasExpressCheckoutTarget) {
      // For subscriptions, use "subscribe" button type and disable PayPal (not supported)
      const isSubscription = this.recurringValue;

      const expressCheckoutOptions = {
        buttonType: {
          applePay: "plain",
          googlePay: "plain",
          paypal: "paypal"
        },
        buttonTheme: {
          applePay: "black",
          googlePay: "black"
        },
        buttonHeight: 48,
        layout: {
          maxColumns: 3,
          overflow: "auto"
        },
        paymentMethods: {
          applePay: "auto",
          googlePay: "auto",
          // PayPal doesn't support subscriptions via Express Checkout
          paypal: isSubscription ? "never" : "auto",
          link: "auto",
          klarna: "never",
          amazonPay: "never"
        }
      };

      if (this.hasCheckoutRepricingUrlValue) {
        expressCheckoutOptions.shippingAddressRequired = true;
        expressCheckoutOptions.allowedShippingCountries = this.#allowedCountriesForExpress();
        expressCheckoutOptions.shippingRates = [
          { id: "digital-delivery", displayName: "Digital delivery", amount: 0 }
        ];
        if (isSubscription) {
          expressCheckoutOptions.paymentMethods = {
            ...expressCheckoutOptions.paymentMethods,
            paypal: "never"
          };
        }
      }

      const expressCheckout = this.expressElements.create("expressCheckout", expressCheckoutOptions);
      expressCheckout.mount(this.expressCheckoutTarget);

      // Validate billing form BEFORE opening wallet popup
      expressCheckout.on("click", (event) => {
        if (!this.#validateBillingFormBeforeExpressCheckout()) {
          // Prevent wallet popup from opening
          event.resolve({ disableDefaultBehavior: true });
        } else {
          event.resolve();
        }
      });

      if (this.hasCheckoutRepricingUrlValue) {
        expressCheckout.on("shippingaddresschange", async (event) => {
          const countryCode = event.address?.country;
          if (!countryCode) {
            event.reject();
            return;
          }

          const repricing = await this.#repriceCheckout({
            countryCode,
            businessAccount: this.#currentBusinessSelection(),
            previewOnly: true
          });
          if (!repricing.ok) {
            event.reject();
            return;
          }

          this.expressCheckoutCountryCode = countryCode;

          event.resolve({
            lineItems: [{
              name: this.paymentDescriptionValue || "Purchase",
              amount: repricing.data?.amounts?.discount_price_cents ?? repricing.data?.amounts?.price_cents ?? 0
            }]
          });
        });

        expressCheckout.on("cancel", () => {
          this.expressCheckoutCountryCode = null;
        });
      }

      expressCheckout.on("confirm", () => this.handleSubmit(null, this.expressElements, {
        countryCode: this.expressCheckoutCountryCode
      }));

      // Check if any express checkout methods are available
      expressCheckout.on("ready", ({ availablePaymentMethods }) => {
        const available = Boolean(availablePaymentMethods);
        this.element.dispatchEvent(
          new CustomEvent("stripe-checkout:express-checkout-ready", {
            bubbles: true,
            detail: { available },
          })
        );

        if (availablePaymentMethods) {
          // Express checkout methods available - show divider
          if (this.hasExpressDividerTarget) {
            this.expressDividerTarget.hidden = false;
          }
        } else {
          // No express checkout methods available - hide express checkout and divider
          this.expressCheckoutTarget.style.display = "none";
          if (this.hasExpressDividerTarget) {
            this.expressDividerTarget.hidden = true;
          }
        }
      });
    } else {
      // No express checkout target - make submit button full width
      this.submitButtonTarget.classList.add("button--full-width");
    }

    // Regular checkout
    const payment = this.elements.create("payment", {
      layout: {
        type: 'accordion',
        spacedAccordionItems: false
      },
      ...this.#paymentElementArgs
    });

    payment.mount(this.paymentTarget);
  }

  async handleSubmit(e, elementsOverride = null, { countryCode = null } = {}) {
    e?.preventDefault();
    this.#setLoading(true);

    // Validate withdrawal checkbox if required (before any server calls)
    if (this.#withdrawalCheckboxRequired() && !this.#withdrawalCheckboxChecked()) {
      this.#showMessage(this.errorWithdrawalRequiredValue || "Please confirm the withdrawal rights waiver.");
      this.#setLoading(false);
      return;
    }

    const checkoutContext = this.#effectiveCheckoutContext({
      countryCode,
      businessAccount: this.#currentBusinessSelection(),
    });

    // Save billing data first if business account form is visible
    const billingFormSaved = await this.#saveBillingFormIfNeeded();
    if (!billingFormSaved) {
      this.#setLoading(false);
      return;
    }

    const eligible = await this.#checkEligibility(checkoutContext);
    if (!eligible) {
      this.#setLoading(false);
      return;
    }

    const snapshotRefreshed = await this.#refreshEligibilitySnapshot(checkoutContext);
    if (!snapshotRefreshed) {
      this.#setLoading(false);
      return;
    }

    const repricing = await this.#repriceCheckout(checkoutContext);
    if (!repricing.ok) {
      this.#setLoading(false);
      return;
    }

    const { error } = await this.#confirm(elementsOverride)

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (!error) {
      // No error means redirect is happening or something unexpected
      this.#setLoading(false);
      return;
    }

    if (error.type === "card_error" || error.type === "validation_error") {
      this.#showMessage(error.message);
    } else {
      this.#showMessage(this.errorUnexpectedValue || "An unexpected error occurred.");
    }

    this.#setLoading(false);
  }

  billingContextChanged(event) {
    if (!this.hasCheckoutRepricingUrlValue) return;

    if (event?.detail) {
      this.billingPreviewContext = {
        countryCode: event.detail.countryCode || null,
        businessAccount: event.detail.businessAccount === true
      };
    } else {
      this.billingPreviewContext = {
        countryCode: this.#currentBillingCountryCode(),
        businessAccount: this.#currentBusinessSelection()
      };
    }

    const pricingKey = this.#currentBillingPricingKey();

    if (!pricingKey) {
      this.#showPendingTaxBreakdowns();
      this.lastBillingPricingKey = null;
      this.requestedBillingPricingKey = null;
      return;
    }

    if (pricingKey === this.lastBillingPricingKey || pricingKey === this.requestedBillingPricingKey) return;

    if (this.lastBillingPricingKey && pricingKey !== this.lastBillingPricingKey) {
      this.#markSummaryAmountsPending();
    }
    this.#showPendingTaxBreakdowns();
    this.requestedBillingPricingKey = pricingKey;
    void this.#syncBillingContextRepricing(pricingKey);
  }

  async #confirm(elementsOverride = null) {
    const params = {
      elements: elementsOverride || this.elements,
      confirmParams: {
        return_url: this.returnUrlValue,
      },
    }

    if (this.setupIntentValue) {
      return await this.stripe.confirmSetup(params);
    } else {
      return await this.stripe.confirmPayment(params);
    }
  }

  async #checkEligibility({ countryCode = null, businessAccount = null } = {}) {
    if (!this.hasCheckoutEligibilityUrlValue) {
      this.#showMessage(
        this.errorEligibilityServiceUnavailableValue ||
          this.errorUnexpectedValue ||
          "Checkout is currently unavailable."
      );
      return false;
    }

    try {
      const url = new URL(this.checkoutEligibilityUrlValue, window.location.origin);
      if (this.eligibilitySnapshotSubjectTypeValue) {
        url.searchParams.set("subject_type", this.eligibilitySnapshotSubjectTypeValue);
        url.searchParams.set("subject_id", this.eligibilitySnapshotSubjectIdValue);
      }
      if (countryCode) {
        url.searchParams.set("country_code", countryCode);
      }
      if (typeof businessAccount === "boolean") {
        url.searchParams.set("business_account", businessAccount ? "true" : "false");
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      });

      let data = null;
      try {
        data = await response.json();
      } catch (_error) {
        data = null;
      }

      if (response.status === 401) {
        this.#showMessage(
          this.errorEligibilityUnauthorizedValue ||
            "Please sign in again to continue checkout."
        );
        return false;
      }

      if (response.status === 403) {
        const reason = data?.reason;
        const message =
          (reason && this.checkoutEligibilityMessagesValue?.[reason]) ||
          this.errorEligibilityServiceUnavailableValue ||
          this.errorUnexpectedValue ||
          "Checkout is currently unavailable.";

        this.#showMessage(message, { persistent: true });
        return false;
      }

      if (!response.ok || response.status >= 500) {
        this.#showMessage(
          this.errorEligibilityServiceUnavailableValue ||
            "Checkout is currently unavailable."
        );
        return false;
      }

      if (data?.eligible === true) return true;

      this.#showMessage(
        this.errorEligibilityServiceUnavailableValue ||
          this.errorUnexpectedValue ||
          "Checkout is currently unavailable."
      );
      return false;
    } catch (_error) {
      this.#showMessage(
        this.errorEligibilityServiceUnavailableValue ||
          this.errorNetworkValue ||
          "Checkout is currently unavailable."
      );
      return false;
    }
  }

  async #refreshEligibilitySnapshot({ countryCode = null, businessAccount = null, silent = false } = {}) {
    // No snapshot subject configured (e.g. non-checkout usages of this controller)
    if (!this.hasEligibilitySnapshotRefreshUrlValue) return true;

    if (!this.hasEligibilitySnapshotSubjectTypeValue || !this.hasEligibilitySnapshotSubjectIdValue) {
      if (!silent) {
        this.#showMessage(
          this.errorEligibilityServiceUnavailableValue ||
            this.errorUnexpectedValue ||
            "Checkout is currently unavailable."
        );
      }
      return false;
    }

    try {
      const response = await fetch(this.eligibilitySnapshotRefreshUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          subject_type: this.eligibilitySnapshotSubjectTypeValue,
          subject_id: this.eligibilitySnapshotSubjectIdValue,
          country_code: countryCode,
          business_account: businessAccount,
          withdrawal_acknowledged: this.#withdrawalCheckboxChecked()
        }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch (_error) {
        data = null;
      }

      if (response.status === 401) {
        if (!silent) {
          this.#showMessage(
            this.errorEligibilityUnauthorizedValue ||
              "Please sign in again to continue checkout."
          );
        }
        return false;
      }

      if (response.status === 422 && data?.error === "withdrawal_acknowledgment_required") {
        if (!silent) {
          this.#showMessage(this.errorWithdrawalRequiredValue || "Please confirm the withdrawal rights waiver.");
        }
        return false;
      }

      if (!response.ok || response.status >= 500) {
        if (!silent) {
          this.#showMessage(
            this.errorEligibilityServiceUnavailableValue ||
              "Checkout is currently unavailable."
          );
        }
        return false;
      }

      if (data?.updated === true) return true;

      if (!silent) {
        this.#showMessage(
          this.errorEligibilityServiceUnavailableValue ||
            this.errorUnexpectedValue ||
            "Checkout is currently unavailable."
        );
      }
      return false;
    } catch (_error) {
      if (!silent) {
        this.#showMessage(
          this.errorEligibilityServiceUnavailableValue ||
            this.errorNetworkValue ||
            "Checkout is currently unavailable."
        );
      }
      return false;
    }
  }

  async #repriceCheckout({ countryCode = null, businessAccount = null, silent = false, applyAmounts = true, previewOnly = false } = {}) {
    if (!this.hasCheckoutRepricingUrlValue) return { ok: true, data: null }

    try {
      const response = await fetch(this.checkoutRepricingUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          subject_type: this.repricingSubjectTypeValue,
          subject_id: this.repricingSubjectIdValue,
          country_code: countryCode,
          business_account: businessAccount,
          preview_only: previewOnly
        }),
      })

      let data = null
      try {
        data = await response.json()
      } catch (_error) {
        data = null
      }

      if (!response.ok) {
        if (!silent) {
          this.#showMessage(
            data?.error ||
              this.errorRepricingFailedValue ||
              this.errorUnexpectedValue ||
              "Checkout is currently unavailable."
          )
        }
        return { ok: false, data }
      }

      if (applyAmounts && data?.repriced && data.amounts) {
        this.#updateDisplayedAmounts(data.amounts, data.price_phases)
      }

      return { ok: true, data }
    } catch (_error) {
      if (!silent) {
        this.#showMessage(
          this.errorRepricingFailedValue ||
            this.errorNetworkValue ||
            this.errorUnexpectedValue ||
            "Checkout is currently unavailable."
        )
      }
      return { ok: false, data: null }
    }
  }

  #showMessage(messageText, { persistent = false } = {}) {
    this.messageContainerTarget.classList.remove("hidden");
    this.messageContainerTarget.textContent = messageText;
    this.messagePersistent = persistent;

    // Clear any existing timeout
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = null;
    }

    // Persistent messages (e.g. eligibility blocks) stay visible until next action
    if (persistent) return;

    // Hide message after 8 seconds
    this.messageTimeout = setTimeout(() => {
      this.messageContainerTarget.classList.add("hidden");
      this.messageContainerTarget.textContent = "";
      this.messagePersistent = false;
    }, 8000);
  }

  #clearMessage({ keepPersistent = true } = {}) {
    if (keepPersistent && this.messagePersistent) return;

    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
      this.messageTimeout = null;
    }

    this.messageContainerTarget.classList.add("hidden");
    this.messageContainerTarget.textContent = "";
    this.messagePersistent = false;
  }

  #withdrawalCheckboxRequired() {
    const sections = this.element.querySelectorAll('[data-checkout-payment-form-target="withdrawalCheckboxSection"]');
    return Array.from(sections).some(section => !section.hidden);
  }

  #withdrawalCheckboxChecked() {
    const checkboxes = this.element.querySelectorAll('[data-checkout-payment-form-target="withdrawalCheckbox"]');
    return Array.from(checkboxes).some(cb => cb.checked);
  }

  #setLoading(isLoading) {
    if (isLoading) {
      // Disable the button and show a spinner
      this.submitButtonTarget.disabled = true;
      this.spinnerTarget.classList.remove("hidden");
      this.buttonTextTarget.classList.add("hidden");
    } else {
      this.submitButtonTarget.disabled = false;
      this.spinnerTarget.classList.add("hidden");
      this.buttonTextTarget.classList.remove("hidden");
    }
  }

  #validateBillingFormBeforeExpressCheckout() {
    // Check withdrawal checkbox before opening wallet
    if (this.#withdrawalCheckboxRequired() && !this.#withdrawalCheckboxChecked()) {
      this.#showMessage(this.errorWithdrawalRequiredValue || "Please confirm the withdrawal rights waiver.");
      return false;
    }

    // Check if billing form exists
    if (!this.hasBillingFormTarget) return true;

    const billingForm = this.billingFormTarget;
    const billingFields = billingForm.closest('[data-checkout-payment-form-target="billingFields"]');
    const isPrivateCustomer = billingFields?.hidden;

    // Private customers don't need billing validation
    if (isPrivateCustomer) return true;

    // Extra validation for tax ID (required attribute is set dynamically via JS,
    // which may not be caught by checkValidity() in all browsers)
    // Tax ID is required for all non-DE business customers (EU reverse charge + CH)
    const countrySelect = billingForm.querySelector('[data-checkout-payment-form-target="countrySelect"]');
    const taxIdInput = billingForm.querySelector('[data-checkout-payment-form-target="taxIdInput"]');
    if (countrySelect?.value !== "DE" && taxIdInput && !taxIdInput.value.trim()) {
      taxIdInput.focus();
      this.#showMessage(this.errorTaxIdRequiredValue || this.errorBillingRequiredValue);
      return false;
    }

    // Validate billing form (client-side HTML5 validation)
    if (!billingForm.checkValidity()) {
      billingForm.reportValidity();
      this.#showMessage(this.errorBillingRequiredValue || "Please fill in all required billing address fields.");
      return false;
    }

    return true;
  }

  async #saveBillingFormIfNeeded({ silent = false } = {}) {
    // Check if billing form exists
    if (!this.hasBillingFormTarget) return true;

    const billingForm = this.billingFormTarget;
    const billingFields = billingForm.closest('[data-checkout-payment-form-target="billingFields"]');
    const isPrivateCustomer = billingFields?.hidden;

    // For private customers, we still need to update business_account to false
    // but skip billing address validation
    if (!isPrivateCustomer) {
      // Extra validation for tax ID (required for all non-DE business customers)
      const countrySelect = billingForm.querySelector('[data-checkout-payment-form-target="countrySelect"]');
      const taxIdInput = billingForm.querySelector('[data-checkout-payment-form-target="taxIdInput"]');
      if (countrySelect?.value !== "DE" && taxIdInput && !taxIdInput.value.trim()) {
        if (!silent) {
          taxIdInput.focus();
          this.#showMessage(this.errorTaxIdRequiredValue || this.errorBillingRequiredValue);
        }
        return false;
      }

      // Validate billing form (client-side) only for business customers
      if (!billingForm.checkValidity()) {
        if (!silent) {
          billingForm.reportValidity();
          this.#showMessage(this.errorBillingRequiredValue || "Please fill in all required billing address fields.");
        }
        return false;
      }
    }

    // Submit billing form via fetch
    // For private customers, this will just set business_account: 0
    // For business customers, this saves the full billing address
    const formData = new FormData(billingForm);

    // Ensure business_account value reflects current selection
    if (isPrivateCustomer) {
      const billingInputs = this.billingBusinessFieldTargets;
      billingInputs.forEach(input => formData.delete(input.name));
      formData.set('user[business_account]', '0');
    }

    try {
      const response = await fetch(billingForm.action, {
        method: 'POST', // Rails uses POST with _method=patch
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        // Try to get error message from server response
        let errorMessage = this.errorBillingSaveFailedValue || "Error saving billing address.";
        try {
          const errorData = await response.json();
          if (errorData.errors) {
            // Rails returns errors as { errors: { field: ["message"] } } or { errors: ["message"] }
            if (Array.isArray(errorData.errors)) {
              errorMessage = errorData.errors.join(", ");
            } else {
              const messages = Object.entries(errorData.errors)
                .map(([field, msgs]) => `${field}: ${msgs.join(", ")}`)
                .join("; ");
              errorMessage = messages || errorMessage;
            }
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // Could not parse JSON, use default message
        }
        if (!silent) {
          this.#showMessage(errorMessage);
        }
        return false;
      }

      if (!isPrivateCustomer) {
        const persistedCountryCode = billingForm.querySelector('[data-checkout-payment-form-target="countrySelect"]')?.value;
        if (persistedCountryCode) {
          this.element.dispatchEvent(
            new CustomEvent("stripe-checkout:billing-country-persisted", {
              bubbles: true,
              detail: { countryCode: persistedCountryCode }
            })
          );
        }
      }

      return true;
    } catch (error) {
      if (!silent) {
        this.#showMessage(this.errorNetworkValue || "Network error while saving billing address.");
      }
      return false;
    }
  }

  async #syncBillingContextRepricing(expectedPricingKey = this.#currentBillingPricingKey()) {
    const pricingKey = expectedPricingKey;

    if (!pricingKey) {
      this.#showPendingTaxBreakdowns();
      this.lastBillingPricingKey = null;
      this.requestedBillingPricingKey = null;
      return;
    }

    const requestId = ++this.billingPreviewRequestId;

    const repricing = await this.#repriceCheckout({
      countryCode: this.#previewBillingCountryCode(),
      businessAccount: this.#previewBusinessSelection(),
      silent: true,
      applyAmounts: false,
      previewOnly: true
    });
    if (requestId !== this.billingPreviewRequestId) return;

    const currentPricingKey = this.#currentBillingPricingKey();
    if (pricingKey !== currentPricingKey) {
      if (this.requestedBillingPricingKey === pricingKey) {
        this.requestedBillingPricingKey = null;
      }
      this.billingContextChanged();
      return;
    }

    if (!repricing.ok) {
      this.requestedBillingPricingKey = null;
      if (this.lastBillingPricingKey && pricingKey !== this.lastBillingPricingKey) {
        this.#markSummaryAmountsPending();
      }
      this.#showPendingTaxBreakdowns();
      return;
    }

    if (repricing.data?.repriced && repricing.data.amounts) {
      this.#updateDisplayedAmounts(repricing.data.amounts, repricing.data.price_phases);
    }

    this.requestedBillingPricingKey = null;
    this.#clearMessage();
    this.lastBillingPricingKey = pricingKey;
  }

  #updateDisplayedAmounts(amounts, pricePhases) {
    this.#updateSummaryAmounts(amounts);
    document.querySelectorAll('[data-checkout-summary-kind="subscription"]').forEach(summary => {
      summary.querySelectorAll('[data-price-phase-position]').forEach(element => {
        const phase = pricePhases?.find(phase => phase.position === Number(element.dataset.pricePhasePosition));
        element.textContent = Number.isInteger(phase?.buyer_amount_cents)
          ? this.#formatCurrency(phase.buyer_amount_cents, summary.dataset.checkoutSummaryCurrency)
          : "…";
      });
    });
    this.#syncMobileCheckoutTermsNote();
    this.element.dispatchEvent(
      new CustomEvent("stripe-checkout:repriced", {
        bubbles: true,
        detail: { amounts },
      })
    );
  }

  #updateSummaryAmounts(amounts) {
    document.querySelectorAll("[data-checkout-summary-kind]").forEach((summary) => {
      const kind = summary.dataset.checkoutSummaryKind;

      if (kind === "purchase") {
        this.#updatePurchaseSummary(summary, amounts);
      } else if (kind === "subscription") {
        this.#updateSubscriptionSummary(summary, amounts);
      }
    });
  }

  #updatePurchaseSummary(summary, amounts) {
    const currency = summary.dataset.checkoutSummaryCurrency || "EUR";
    const originalPrice = summary.querySelector('[data-checkout-summary-field="original-price"]');
    const discountAmount = summary.querySelector('[data-checkout-summary-field="discount-amount"]');
    const totalPrice = summary.querySelector('[data-checkout-summary-field="total-price"]');
    const vatLabel = summary.querySelector('[data-checkout-summary-field="vat-label"]');

    if (originalPrice) {
      originalPrice.textContent = this.#formatCurrency(amounts.price_cents, currency);
    }

    if (discountAmount) {
      discountAmount.textContent = `- ${this.#formatCurrency(amounts.discount_cents ?? 0, currency)}`;
    }

    if (totalPrice) {
      totalPrice.textContent = this.#formatCurrency(amounts.discount_price_cents ?? amounts.price_cents, currency);
    }

    if (vatLabel) {
      vatLabel.textContent = this.#formatVatLabel(summary, amounts);
    }

    this.#updateTaxBreakdown(summary, amounts);
  }

  #updateSubscriptionSummary(summary, amounts) {
    const currency = summary.dataset.checkoutSummaryCurrency || "EUR";
    const productPrice = this.#replaceTemplate(
      summary.dataset.checkoutSummaryPricePerPeriodTemplate,
      "__PRICE__",
      this.#formatCurrency(amounts.price_cents, currency)
    );
    const totalPrice = this.#formatCurrency(amounts.discount_price_cents ?? amounts.price_cents, currency);
    const discountAmount = this.#formatCurrency(amounts.discount_cents ?? 0, currency);
    const discountPrice = this.#formatCurrency(amounts.discount_price_cents ?? amounts.price_cents, currency);

    // Only normal net subscriptions retain a static catalog header. Phase
    // contracts display the repriced buyer amount for their first section.
    if (!this.#hasStaticNetSubscriptionHeader(summary)) {
      summary.querySelectorAll('[data-checkout-summary-field="product-price"]').forEach((element) => {
        element.textContent = productPrice;
      });
    }

    const discountAmountElement = summary.querySelector('[data-checkout-summary-field="discount-amount"]');
    if (discountAmountElement) {
      discountAmountElement.textContent = `- ${discountAmount}`;
    }

    const totalPriceElement = summary.querySelector('[data-checkout-summary-field="total-price"]');
    if (totalPriceElement) {
      totalPriceElement.textContent = totalPrice;
    }

    const vatLabelElement = summary.querySelector('[data-checkout-summary-field="vat-label"]');
    if (vatLabelElement) {
      vatLabelElement.textContent = this.#formatVatLabel(summary, amounts);
    }

    const priceDetailsElement = summary.querySelector('[data-checkout-summary-field="price-details"]');
    if (priceDetailsElement) {
      const hasDiscount = Number(amounts.discount_cents ?? 0) > 0;

      if (hasDiscount) {
        priceDetailsElement.textContent = this.#replaceTemplate(
          this.#replaceTemplate(
            summary.dataset.checkoutSummaryDiscountPriceDetailsTemplate,
            "__DISCOUNT_PRICE__",
            discountPrice
          ),
          "__PRICE__",
          this.#formatCurrency(amounts.price_cents, currency)
        );
      } else {
        priceDetailsElement.textContent = this.#replaceTemplate(
          summary.dataset.checkoutSummaryPriceDetailsTemplate,
          "__PRICE__",
          this.#formatCurrency(
            amounts.discount_price_cents ?? amounts.price_cents,
            currency,
            summary.dataset.checkoutSummaryPriceDetailsShowCents === "true"
          )
        );
      }
    }

    this.#updateTaxBreakdown(summary, amounts);
  }

  #updateTaxBreakdown(summary, amounts) {
    const currency = summary.dataset.checkoutSummaryCurrency || "EUR";
    const breakdownRows = summary.querySelectorAll('[data-checkout-summary-field="tax-breakdown"]');
    const pendingNote = summary.querySelector('[data-checkout-summary-field="tax-breakdown-pending"]');
    const netCents = Number.isFinite(Number(amounts.discount_net_cents))
      ? amounts.discount_net_cents
      : amounts.net_cents;
    const showBreakdown = Number.isFinite(Number(netCents));
    const vatLabel = summary.querySelector('[data-checkout-summary-field="vat-label"]');

    breakdownRows.forEach((row) => {
      row.style.display = showBreakdown ? "" : "none";
    });

    if (pendingNote) {
      pendingNote.hidden = true;
    }

    if (vatLabel) {
      vatLabel.style.display = showBreakdown ? "none" : "";
    }

    if (showBreakdown) {
      const netPrice = summary.querySelector('[data-checkout-summary-field="net-price"]');
      if (netPrice) {
        netPrice.textContent = this.#formatCurrency(netCents, currency);
      }

      const taxAmount = summary.querySelector('[data-checkout-summary-field="tax-amount"]');
      if (taxAmount) {
        taxAmount.textContent = this.#formatCurrency(amounts.tax_cents, currency);
      }

      const taxAmountLabel = summary.querySelector('[data-checkout-summary-field="tax-amount-label"]');
      if (taxAmountLabel) {
        taxAmountLabel.textContent = this.#formatTaxBreakdownLabel(summary, amounts);
      }
    }
  }

  #hideTaxBreakdowns() {
    document.querySelectorAll("[data-checkout-summary-kind]").forEach((summary) => {
      summary.querySelectorAll('[data-checkout-summary-field="tax-breakdown"]').forEach((row) => {
        row.style.display = "none";
      });

      const pendingNote = summary.querySelector('[data-checkout-summary-field="tax-breakdown-pending"]');
      if (pendingNote) {
        pendingNote.hidden = true;
      }

      const vatLabel = summary.querySelector('[data-checkout-summary-field="vat-label"]');
      if (vatLabel) {
        vatLabel.style.display = "";
      }
    });
  }

  #showPendingTaxBreakdowns() {
    document.querySelectorAll("[data-checkout-summary-kind]").forEach((summary) => {
      summary.querySelectorAll('[data-checkout-summary-field="tax-breakdown"]').forEach((row) => {
        row.style.display = "none";
      });

      const pendingNote = summary.querySelector('[data-checkout-summary-field="tax-breakdown-pending"]');
      if (pendingNote) {
        pendingNote.hidden = false;
      }

      const vatLabel = summary.querySelector('[data-checkout-summary-field="vat-label"]');
      if (vatLabel) {
        vatLabel.style.display = "none";
      }
    });
  }

  #hasStaticNetSubscriptionHeader(summary) {
    return summary.dataset.checkoutSummaryKind === "subscription" &&
      summary.dataset.checkoutSummaryNetPricing === "true" &&
      !summary.querySelector('[data-price-phase-position]');
  }

  #markSummaryAmountsPending() {
    const pendingFields = [
      "original-price",
      "discount-amount",
      "total-price",
      "product-price",
      "net-price",
      "tax-amount",
      "price-details",
      "phase-price",
    ];

    document.querySelectorAll("[data-checkout-summary-kind]").forEach((summary) => {
      pendingFields.forEach((field) => {
        if (field === "product-price" && this.#hasStaticNetSubscriptionHeader(summary)) return;
        summary.querySelectorAll(`[data-checkout-summary-field="${field}"]`).forEach((element) => {
          element.textContent = "…";
        });
      });
    });
    this.#syncMobileCheckoutTermsNote();
  }

  #syncMobileCheckoutTermsNote() {
    if (!this.hasMobileCheckoutTermsNoteTarget) return;

    const source = document.querySelector('[data-checkout-summary-field="subscription-terms-note"]');
    const text = source?.textContent?.trim() || "";

    // Clone the rendered terms so phase rows and repriced amounts stay structured.
    this.mobileCheckoutTermsNoteTarget.replaceChildren(
      ...Array.from(source?.childNodes || [], node => node.cloneNode(true))
    );
    this.mobileCheckoutTermsNoteTarget.hidden = text.length === 0;
  }

  #formatCurrency(cents, currency, showCents = false) {
    const amount = Number(cents || 0) / 100;
    const wholeAmount = Number(cents || 0) % 100 === 0;

    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency,
      minimumFractionDigits: wholeAmount && !showCents ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  #formatVatLabel(summary, amounts) {
    if (Number(amounts.tax_rate) > 0) {
      return this.#replaceTemplate(
        summary.dataset.checkoutSummaryIncludingVatRateTemplate,
        "__RATE__",
        this.#formatTaxRate(amounts.tax_rate)
      );
    }

    if (amounts.business_account && !this.#taxExempt(summary) && summary.dataset.checkoutSummaryReverseChargeLabel) {
      return summary.dataset.checkoutSummaryReverseChargeLabel;
    }

    return summary.dataset.checkoutSummaryZeroTaxLabel || "";
  }

  #formatTaxBreakdownLabel(summary, amounts) {
    if (Number(amounts.tax_rate) > 0) {
      return this.#replaceTemplate(
        summary.dataset.checkoutSummaryVatAmountTemplate,
        "__RATE__",
        this.#formatTaxRate(amounts.tax_rate)
      );
    }

    if (amounts.business_account && !this.#taxExempt(summary) && summary.dataset.checkoutSummaryReverseChargeLabel) {
      return summary.dataset.checkoutSummaryReverseChargeLabel;
    }

    return summary.dataset.checkoutSummaryZeroTaxLabel || "";
  }

  #taxExempt(summary) {
    return summary.dataset.checkoutSummaryTaxExempt === "true";
  }

  #formatTaxRate(rate) {
    const numericRate = Number(rate || 0);
    return Number.isInteger(numericRate) ? String(numericRate) : numericRate.toFixed(1).replace(/\.0$/, "");
  }

  #replaceTemplate(template, placeholder, value) {
    if (!template) return "";

    return template.replaceAll(placeholder, value);
  }

  #allowedCountriesForExpress() {
    const countrySelect = this.billingFormTarget?.querySelector('[name="user[billing_address_country_code]"]');
    if (!countrySelect) return [];

    return Array.from(countrySelect.options)
      .map((option) => option.value)
      .filter((value) => value && value.length > 0);
  }

  #currentBillingCountryCode() {
    if (!this.hasBillingFormTarget) return null;

    const countrySelect = this.billingFormTarget.querySelector('[data-checkout-payment-form-target="countrySelect"]');
    return countrySelect?.value || null;
  }

  #currentBusinessSelection() {
    if (!this.hasBillingFormTarget) return false;

    const businessAccountField = this.billingFormTarget.querySelector('[data-checkout-payment-form-target="businessAccountField"]');
    return businessAccountField?.value === "1";
  }

  #currentBillingPricingKey() {
    const countryCode = this.#previewBillingCountryCode();
    if (!countryCode) return null;

    return [
      this.#previewBusinessSelection() ? "business" : "private",
      countryCode
    ].join(":");
  }

  #previewBillingCountryCode() {
    return this.billingPreviewContext?.countryCode || this.#currentBillingCountryCode();
  }

  #previewBusinessSelection() {
    if (this.billingPreviewContext && typeof this.billingPreviewContext.businessAccount === "boolean") {
      return this.billingPreviewContext.businessAccount;
    }

    return this.#currentBusinessSelection();
  }

  #effectiveCheckoutContext({ countryCode = null, businessAccount = null } = {}) {
    return {
      countryCode: countryCode || this.#previewBillingCountryCode(),
      businessAccount:
        typeof businessAccount === "boolean" ? businessAccount : this.#previewBusinessSelection(),
    };
  }

  get #paymentElementArgs() {
    return {
      ...this.#walletArgs,
      ...this.#appleArgs
    }
  }

  get #walletArgs() {
    // Show Apple Pay and Google Pay in Payment Element, hide Link
    return {
      wallets: {
        applePay: "auto",
        googlePay: "auto",
        link: "never",
      },
    }
  }

  get #appleArgs() {
    if (!this.recurringValue) return {};

    return {
      applePay: {
        recurringPaymentRequest: {
          paymentDescription: this.paymentDescriptionValue,
          managementURL: this.managementUrlValue,
          ...this.#appleRegularBillingArgs,
          ...this.#appleTrialBillingArgs
        }
      }
    }
  }

  get #appleRegularBillingArgs() {
    const regularProps = this.regularBillingPropsValue

    if (regularProps.recurringPaymentStartDate) {
      regularProps.recurringPaymentStartDate = new Date(regularProps.recurringPaymentStartDate)
    }

    return {
      regularBilling: regularProps
    }
  }

  get #appleTrialBillingArgs() {
    if (!this.hasTrialBillingPropsValue) return {}

    const trialProps = this.trialBillingPropsValue
    if (Object.keys(trialProps).length === 0) return {}

    if (trialProps.recurringPaymentEndDate) {
      trialProps.recurringPaymentEndDate = new Date(trialProps.recurringPaymentEndDate)
    }

    return {
      trialBilling: trialProps
    }
  }
}
